import React, { createContext, useState, useContext, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Roles } from "../enum/roles";
import { comparePassword } from "@/util/cripto";

interface User {
  id: number;
  nome: string;
  nivel: Roles;
  cpf: string; // Sempre será apenas números no frontend
  ra: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (
    identifier: string,
    password: string,
    isRa: boolean
  ) => Promise<{ error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored user on mount
    const storedUser = localStorage.getItem("usuario");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Função para formatar CPF com máscara (para consulta no banco)
  const formatCPF = (cpf: string) => {
    const numericValue = cpf.replace(/\D/g, ""); // Remove não numéricos
    if (numericValue.length === 11) {
      return `${numericValue.slice(0, 3)}.${numericValue.slice(
        3,
        6
      )}.${numericValue.slice(6, 9)}-${numericValue.slice(9)}`;
    }
    return numericValue; // Retorna apenas números se não tiver 11 dígitos
  };

  const login = async (identifier: string, password: string, isRA: boolean) => {
    try {
      let query;
      if (isRA) {
        query = supabase
          .from("usuarios")
          .select("id, nome, senha, nivel, cpf, ra")
          .eq("ra", identifier);
      } else {
        const isEmail = identifier.includes("@");
        if (isEmail) {
          query = supabase
            .from("usuarios")
            .select("id, nome, senha, nivel, cpf, ra")
            .eq("email", identifier);
        } else {
          // Formata o CPF com máscara para consultar no banco
          const formattedCPF = formatCPF(identifier);
          query = supabase
            .from("usuarios")
            .select("id, nome, senha, nivel, cpf, ra")
            .eq("cpf", formattedCPF);
        }
      }

      const { data: userData, error: userError } = await query.single();

      if (userError || !userData) {
        return { error: "Usuário não encontrado!" };
      }

      const isPasswordValid = await comparePassword(password, userData.senha);
      if (!isPasswordValid) {
        return { error: "Senha incorreta!" };
      }

      // Normaliza o CPF retornado do banco para armazenar apenas números no frontend
      const newUser = {
        id: userData.id,
        nome: userData.nome,
        nivel: userData.nivel,
        cpf: userData.cpf,
        ra: userData.ra,
      };

      setUser(newUser);
      localStorage.setItem("usuario", JSON.stringify(newUser));
      return {};
    } catch (err) {
      console.error("Erro ao tentar logar:", err);
      return { error: "Erro inesperado. Tente novamente mais tarde." };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("usuario");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
